import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Build as ToolIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

const CATEGORY_LABELS = {
  ai_interaction_issue: 'AI Interaction Issue',
  technical_problem: 'Technical Problem',
  user_info_request: 'User Info Request',
  clinical_concern: 'Clinical Concern',
  general_feedback: 'General Feedback'
};

const CATEGORY_COLORS = {
  ai_interaction_issue: '#e65100',
  technical_problem: '#d32f2f',
  user_info_request: '#1565c0',
  clinical_concern: '#b71c1c',
  general_feedback: '#2e7d32'
};

const AasMessage = ({ message }) => {
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const isStaff = message.role === 'staff';
  const isAas = message.role === 'aas';

  // Check if any tool call was classify_ticket
  const classificationCall = (message.toolCalls || []).find(tc => tc.name === 'classify_ticket' || tc.toolName === 'classify_ticket');
  const classification = classificationCall?.output?.classification || classificationCall?.result?.classification || null;

  // Check if TASS was consulted
  const tassCall = (message.toolCalls || []).find(tc => tc.name === 'consult_tass' || tc.toolName === 'consult_tass');
  const tassUsed = !!tassCall;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isStaff ? 'flex-end' : 'flex-start',
        mb: 1.5
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 1.5,
          maxWidth: '85%',
          bgcolor: isStaff ? '#e3f2fd' : '#f5f5f5',
          borderRadius: 2,
          borderTopRightRadius: isStaff ? 0 : 2,
          borderTopLeftRadius: isAas ? 0 : 2
        }}
      >
        {/* Role label */}
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
          {isStaff ? 'You' : 'AAS'}
        </Typography>

        {/* Message content */}
        {isAas ? (
          <Box sx={{ '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 }, '& ul, & ol': { mt: 0, mb: 1, pl: 2.5 }, '& h1, & h2, & h3, & h4': { mt: 1, mb: 0.5, fontSize: '0.95rem', fontWeight: 600 }, fontSize: '0.875rem', lineHeight: 1.6 }}>
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        )}

        {/* TASS Analysis badge */}
        {tassUsed && (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label="TASS Clinical Analysis"
              sx={{
                bgcolor: '#5c6bc0',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        )}

        {/* Classification badge */}
        {classification && (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`Classification: ${CATEGORY_LABELS[classification.category] || classification.category}`}
              sx={{
                bgcolor: CATEGORY_COLORS[classification.category] || '#666',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
            {classification.confidence != null && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {(classification.confidence * 100).toFixed(0)}% confidence
              </Typography>
            )}
          </Box>
        )}

        {/* Confidence indicator */}
        {message.confidence != null && !classification && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Confidence: {(message.confidence * 100).toFixed(0)}%
          </Typography>
        )}

        {/* Tool calls accordion */}
        {isAas && message.toolCalls && message.toolCalls.length > 0 && (
          <Accordion
            expanded={toolsExpanded}
            onChange={() => setToolsExpanded(!toolsExpanded)}
            sx={{ mt: 1, bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
              sx={{ minHeight: 28, p: 0, '& .MuiAccordionSummary-content': { m: 0 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ToolIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {message.toolCalls.length} tool call{message.toolCalls.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pt: 0.5 }}>
              {message.toolCalls.map((tc, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.25,
                    borderBottom: idx < message.toolCalls.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Chip
                    label={tc.name || tc.toolName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                  {tc.durationMs != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <TimerIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        {tc.durationMs}ms
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    label={tc.permissionLevel || 'read'}
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 16, bgcolor: tc.permissionLevel === 'safe_action' ? '#fff3e0' : '#e8f5e9' }}
                  />
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Timestamp */}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, textAlign: isStaff ? 'right' : 'left' }}>
          {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
        </Typography>
      </Paper>
    </Box>
  );
};

export default AasMessage;
